import { getEntityVarietyId } from 'services/entities/utils';
import { EntitiesKeyedById } from 'services/entities/types';
import { SchemaEntityTypeId } from 'services/schemas/types';
import { VarietyId } from 'services/varieties/types';
import omit from 'lodash.omit';
import {
    SprayEventApplicationType,
    SprayEventApplicationTypeDisplay,
    SprayEventEntities,
    SprayEventOperation,
    SprayEventPatchRequestPayload,
    SprayEventPostRequestPayload
} from './types';

export const getSprayEventVarietiesArray = (
    sprayEventEntities: SprayEventEntities | undefined,
    schemaEntityTypeId: SchemaEntityTypeId,
    entities: EntitiesKeyedById
): VarietyId[] => {
    if (sprayEventEntities && schemaEntityTypeId) {
        const varietiesArray = sprayEventEntities.reduce((result, entity) => {
            const res = [...result];
            const varietyId = getEntityVarietyId(
                entity.id,
                entities,
                schemaEntityTypeId
            );

            if (varietyId) res.push(varietyId);

            return res;
        }, [] as VarietyId[]);

        return varietiesArray;
    }

    return [];
};

export const getSprayEventApplicationTypeDisplay = (
    applicationType: string
): string => {
    switch (applicationType.toLowerCase()) {
        case SprayEventApplicationType.MULTIOPERATOR.toLowerCase():
            return SprayEventApplicationTypeDisplay.MULTIOPERATOR;
        case SprayEventApplicationType.TWODIMENSION.toLowerCase():
            return SprayEventApplicationTypeDisplay.TWODIMENSION;
        case SprayEventApplicationType.STANDARD.toLowerCase():
        default:
            return SprayEventApplicationTypeDisplay.STANDARD;
    }
};

export const isStandardApplicationType = (applicationType: string): boolean =>
    doesApplicationTypeMatched(
        applicationType,
        SprayEventApplicationType.STANDARD
    );

export const isMultiOperatorApplicationType = (
    applicationType: string
): boolean =>
    doesApplicationTypeMatched(
        applicationType,
        SprayEventApplicationType.MULTIOPERATOR
    );

export const is2DApplicationType = (applicationType: string): boolean =>
    doesApplicationTypeMatched(
        applicationType,
        SprayEventApplicationType.TWODIMENSION
    );

export const isApplicationTypeNeedHalfCalculation = (
    applicationType: string
): boolean =>
    is2DApplicationType(applicationType) ||
    isMultiOperatorApplicationType(applicationType);

export const doesApplicationTypeMatched = (
    applicationType: string,
    shouldMatchApplicationType: string
): boolean =>
    applicationType?.toLowerCase() ===
    shouldMatchApplicationType?.toLowerCase();

export const mapSprayEventFromPayload = (
    payload: SprayEventPostRequestPayload | SprayEventPatchRequestPayload
): SprayEventPostRequestPayload | SprayEventPatchRequestPayload => {
    return {
        ...payload,
        sprayEventOperations: payload.sprayEventOperations?.map(
            (sprayEventOperation) => {
                return {
                    ...sprayEventOperation,
                    twoDimensionStartingRow: is2DApplicationType(
                        payload.applicationType
                    )
                        ? sprayEventOperation.startingRow
                        : undefined
                };
            }
        )
    };
};

export const formatSprayEventPayloads = (
    sprayEvent: SprayEventPostRequestPayload | SprayEventPatchRequestPayload
): SprayEventPostRequestPayload | SprayEventPatchRequestPayload => {
    if (
        (isStandardApplicationType(sprayEvent.applicationType) ||
            is2DApplicationType(sprayEvent.applicationType)) &&
        sprayEvent.sprayEventOperations.length === 2
    ) {
        sprayEvent.sprayEventOperations.pop();
    }

    return {
        ...sprayEvent,
        sprayEventOperations: sprayEvent.sprayEventOperations
            .map((sprayEventOperation) => {
                return {
                    ...sprayEventOperation,
                    startingRow: is2DApplicationType(sprayEvent.applicationType)
                        ? sprayEventOperation.twoDimensionStartingRow
                        : sprayEventOperation.startingRow
                };
            })
            .map((sprayEventOperation) =>
                omit(sprayEventOperation, [
                    'eventId',
                    'sprayEventId',
                    'twoDimensionStartingRow'
                ])
            ) as SprayEventOperation[]
    };
};
